import React, { useEffect, useRef, useState } from 'react';
import TableComponent from 'components/table-component/Table';
import Error from 'components/Error';
import { useTranslation } from 'react-i18next';
import TicketListHeaders from './TicketListHeaders';
import { cloneDeep } from 'lodash';
import { ticketHeaderList } from '../utils';
import Pagination from 'components/common-components/Pagination';
import { CallAPI } from 'actions/General';
import ticketGreyIcon from 'assets/images/icons/ticket-grey.svg';

const TicketsList = () => {
  const { t } = useTranslation();

  const [ticketsList, setTicketsList] = useState([]);
  const [selectedRecordIds, setSelectedRecordIds] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [headerArray, setHeaderArray] = useState(cloneDeep(ticketHeaderList));
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState({});
  const [APIError, setAPIError] = useState('');
  const [totalRecords, setTotalRecords] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [displayEntries, setDisplayEntries] = useState(10);

  const companyFieldsTitleLookup = useRef({});

  const changeDisplayEntries = (name, val) => {
    setCurrentPage(1);
    setDisplayEntries(parseInt(val, 10));
    fetchTicketsList(1, parseInt(val, 10));
  };

  const handleDebouncedFilter = (name, value) => {
    setFilter((prev) => ({ ...prev, [name]: value }));
  };

  const fetchTicketsList = async (page = 1, limit = displayEntries) => {
    setSelectedRecordIds([]);
    setSelectedRows([]);
    setLoading(true);
    const payload = { page, limit };
    if (filter.search) payload.searchText = filter.search;
    if (filter.status) payload.status = filter.status;
    if (filter.priority) payload.priority = filter.priority;
    if (filter.sortBy) payload.sortBy = filter.sortBy;
    if (filter.unassigned) payload.unassigned = true;
    if (filter.overdue) payload.overdue = true;
    if (filter.ticketType) payload.ticketType = filter.ticketType;

    // if (filter.sort) payload.sort = filter.sort;

    // CREATED DATE
    if (filter.createdDateData?.type && filter.createdDateData?.type !== 'all') {
      payload.createdDate = filter.createdDateData.type;
      if (filter.createdDateData?.type === 'custom') {
        const range = filter.createdDateData.range;
        payload.createdFromDate = range[0];
        payload.createdToDate = range[1];
      }
    }

    // DUE DATE
    if (filter.dueDateData?.type && filter.dueDateData?.type !== 'all') {
      payload.dueDate = filter.dueDateData.type;
      if (filter.dueDateData?.type === 'custom') {
        const range = filter.dueDateData.range;
        payload.dueFromDate = range[0];
        payload.dueToDate = range[1];
      }
    }

    const result = await CallAPI('GET_ALL_TICKETS', payload, null, null, setAPIError);
    if (result.status) {
      if (Object.keys(companyFieldsTitleLookup.current).length === 0) {
        const response = await CallAPI('GET_ALL_COMPANY_FIELDS', {});
        if (response.status) {
          const lookup = {};
          response.data.forEach((item) => {
            lookup[item._id] = item.fieldTitle;
          });
          companyFieldsTitleLookup.current = lookup;
        }
      }

      setTicketsList(result.data.tickets);
      if (page === 1) setTotalRecords(result.data.totalCount);
    }
    setLoading(false);
  };

  const onRowsSelection = (name, value) => {
    let updatedSelectedIds = [...selectedRecordIds];
    let updatedSelectedRows = [...selectedRows];
    if (name === 'all') {
      if (value) {
        updatedSelectedIds = ticketsList.map((row) => row._id);
        updatedSelectedRows = ticketsList.map((row) => ({ _id: row._id, status: row.status }));
      } else {
        updatedSelectedIds = [];
        updatedSelectedRows = [];
      }
    } else {
      const row = ticketsList.find((r) => r._id === name);
      if (value) {
        if (!updatedSelectedIds.includes(name)) {
          updatedSelectedIds.push(name);
          updatedSelectedRows.push({ _id: row._id, status: row.status });
        }
      } else {
        updatedSelectedIds = updatedSelectedIds.filter((id) => id !== name);
        updatedSelectedRows = updatedSelectedRows.filter((r) => r._id !== name);
      }
    }
    setSelectedRecordIds(updatedSelectedIds);
    setSelectedRows(updatedSelectedRows);
  };

  const resetHeader = () => {
    setHeaderArray(cloneDeep(ticketHeaderList));
  };

  const resolveCompanyFieldsTitles = () => {
    return companyFieldsTitleLookup.current;
  };

  useEffect(() => {
    fetchTicketsList(1, displayEntries);
  }, [filter]);

  return (
    <div className='tickets-table'>
      {APIError && <Error msg={APIError} />}

      <TicketListHeaders
        filter={filter}
        setFilter={setFilter}
        handleDebouncedFilter={handleDebouncedFilter}
        selectedRecordIds={selectedRecordIds}
        selectedRows={selectedRows}
        actionsCallBack={fetchTicketsList}
      />

      <TableComponent
        header={headerArray}
        data={ticketsList}
        props={{
          translateValueFunc: (val) => t(val),
          resetHeader: resetHeader,
          resolveCompanyFieldsTitles,
        }}
        totalRecords={0}
        loader={loading}
        filterEnabled={false}
        isLoading={loading}
        isFieldSelectionEnabled={true}
        isPaginationEnabled={false}
        isActionsEnabled={true}
        isSelectionEnabled={true}
        selectionKey={'_id'}
        onSelection={onRowsSelection}
        selectedRows={selectedRecordIds}
        noDataComponent={
          <div className='lh-1 w-100 d-flex flex-column gap-2 justify-content-center align-items-center'>
            <img className='theme-width-35' src={ticketGreyIcon} />
            <span className='theme-text-black-1 theme-size-1_3'>{t('text_no_tickets')}</span>
            <span>{t('text_create_tickets_from_mobile_app_forms')}</span>
          </div>
        }
      />

      {totalRecords > 0 && (
        <Pagination
          totalRecords={totalRecords}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          displayEntries={displayEntries}
          setDisplayEntries={changeDisplayEntries}
          currentPageRecords={ticketsList.length}
          paginationFunction={fetchTicketsList}
        />
      )}
    </div>
  );
};

export default TicketsList;
